<script lang="ts" setup>
import { Batch } from '@/core/collections/batches/batch.interface';
import { computed, ref, watch, watchEffect } from 'vue';
import BatchJobNotification from './BatchJobNotification.vue';

//#region Globals
const { state, commit, dispatch } = useStore();
//#endregion

//#region Get Batches
const userInfo = computed(() => state.userInfo?.userInfo);

watchEffect(getActiveBatches);

async function getActiveBatches() {
   if (!userInfo.value) return;
   return await dispatch('batch/getBatches');
}
//#endregion

//#region Toggle All Batches
const showBatchJobs = ref(false);

function toggleBatchJobs() {
   showBatchJobs.value = !showBatchJobs.value;

   if (showBatchJobs.value) {
      getActiveBatches();
   }
}
//#endregion

//#region Batches
const batches = computed(() => state.batch?.batches || []);
const runningBatches = computed(() => batches.value.filter((b) => b?.job_batch?.pending_jobs > 0));

watch(batches, resetShowBatchJobsWhenAllDeleted, { deep: true });
watch(batches, newBatchAdded, { deep: true });

function resetShowBatchJobsWhenAllDeleted(batches: Array<Batch>) {
   if (batches.length === 0) showBatchJobs.value = false;
}

function newBatchAdded() {
   showBatchJobs.value = true;
}
//#endregion
</script>

<template>
   <div class="batch-jobs">
      <NxtIconButton
         :class="[!!runningBatches.length ? 'fa-spin' : '']"
         :name="'arrows-rotate'"
         :disabled="!runningBatches.length"
         :hover-text="$t('batches.running-batches', { count: runningBatches.length || $t('generic.none') }, runningBatches.length)"
         @on-click="toggleBatchJobs"
      />

      <TransitionGroup v-if="showBatchJobs" name="slide" mode="out-in" tag="div" :class="['batch-jobs__list']">
         <BatchJobNotification
            v-for="batch in batches"
            :key="batch.id"
            :batch="batch"
            class="batch-job__list__item"
            @on-delete-batch="commit('batch/REMOVE_BATCH', $event)"
         />
      </TransitionGroup>
   </div>
</template>

<style lang="scss" scoped>
.batch-jobs {
   position: relative;
}

.batch-jobs__icon {
   color: var(--nxt-dark-grey);
}

.batch-jobs__list {
   position: fixed;
   z-index: calc(var(--zindex-fixed) + 1);
   display: flex;
   flex-direction: column;
   margin-top: var(--nxt-page-header-offset);
   top: 0;
   right: var(--nxt-gutter-large);
   gap: var(--nxt-gutter-small);

   max-height: 100vh;
   overflow: auto;

   &::-webkit-scrollbar {
      display: none;
   }

   -ms-overflow-style: none;
   scrollbar-width: none;
}
</style>
